import { useRef, useEffect, useCallback } from "react";

const ScrollProgress = () => {
  const progressRef = useRef<HTMLDivElement>(null);
  const currentProgress = useRef(0);
  const targetProgress = useRef(0);
  const isAnimating = useRef(false);

  const animate = useCallback(() => {
    if (!progressRef.current) return;

    // Smooth interpolation towards target
    const diff = targetProgress.current - currentProgress.current;
    currentProgress.current += diff * 0.15;

    // Apply transform with GPU acceleration
    progressRef.current.style.transform = `scaleX(${currentProgress.current})`;

    // Continue animation only if needed
    if (Math.abs(diff) > 0.0001) {
      requestAnimationFrame(animate);
    } else {
      isAnimating.current = false;
      currentProgress.current = targetProgress.current;
      progressRef.current.style.transform = `scaleX(${targetProgress.current})`;
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      targetProgress.current = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;

      // Start animation loop if not already running
      if (!isAnimating.current) {
        isAnimating.current = true;
        requestAnimationFrame(animate);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [animate]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] h-1 bg-charcoal-light/50 pointer-events-none">
      <div
        ref={progressRef}
        className="h-full w-full origin-left will-change-transform bg-gradient-to-r from-gold via-gold-light to-gold"
        style={{ 
          transform: 'scaleX(0)',
          boxShadow: '0 0 10px hsl(var(--gold) / 0.7), 0 0 20px hsl(var(--gold) / 0.4)',
        }}
      />
    </div>
  );
};

export default ScrollProgress;
